/**
 * Working out where a published post actually lives on the network.
 *
 * The server is gaining a stored permalink (OP#2484); until every provider
 * fills it, some ids can be turned into a URL here without guessing — a
 * Facebook post id is enough to address the post, an Instagram media id is not.
 * Anything uncertain returns null so the UI shows no link rather than a broken
 * one.
 */

/**
 * Providers whose publish id maps to a URL by simple substitution.
 */
const FROM_PUBLISH_ID = {
  facebook: (id) => `https://www.facebook.com/${id}`,
  youtube: (id) => `https://www.youtube.com/watch?v=${id}`,
  linkedin: (id) =>
    String(id).startsWith("urn:li:")
      ? `https://www.linkedin.com/feed/update/${id}`
      : null,
};

/**
 * The URL of a published post, or null when we cannot know it.
 *
 * A stored permalink always wins; only a successful task can have one.
 *
 * @param {Object} task - Post task ({ status, social_type, publish_id, permalink? }).
 * @returns {string|null} An absolute URL, or null when there is nothing to open.
 */
export function resolvePostPermalink(task) {
  if (!task) return null;

  const failed = task.status === "fail" || task.result?.success === false;
  if (failed || task.status !== "success") return null;

  const stored = task.permalink || task.post_url || task.result?.permalink;
  if (typeof stored === "string" && /^https?:\/\//i.test(stored)) return stored;

  const publishId = task.publish_id;
  if (!publishId) return null;

  const build = FROM_PUBLISH_ID[String(task.social_type || "").toLowerCase()];
  return build ? build(publishId) : null;
}
